<!--
  Renders the global notification queue (issue #1963, Phase 1).

  Mounted once at the app root (App.vue). Entries pushed onto the notification
  store via the shared error handler / `notify*` helpers are shown one at a
  time in FIFO order. This is the single display surface for the shared API
  error layer — components no longer own their own error snackbars.
-->
<template>
  <div class="global-notification-host">
    <v-snackbar
      v-for="notification in activeNotifications"
      :key="notification.id"
      :model-value="true"
      :color="colorFor(notification.type)"
      :timeout="notification.timeout"
      location="top"
      data-testid="global-notification"
      :data-notification-type="notification.type"
      @update:model-value="(value: boolean) => onModelValue(notification.id, value)"
    >
      <div class="d-flex flex-column">
        <span data-testid="global-notification-message">{{ notification.message }}</span>
        <span v-if="notification.requestId" class="text-body-small text-medium-emphasis">
          Ref: {{ notification.requestId }}
        </span>
      </div>
      <template #actions>
        <v-btn
          variant="text"
          :icon="mdiClose"
          aria-label="Close"
          data-testid="global-notification-dismiss"
          @click="store.dismiss(notification.id)"
        />
      </template>
    </v-snackbar>
  </div>
</template>

<script setup lang="ts">
import { mdiClose } from "@mdi/js";
import { computed } from "vue";

import { useNotificationStore } from "@/core/api/notificationStore";
import type { NotificationType } from "@/core/api/notificationStore";

const store = useNotificationStore();

// While a backlog waits behind the head, the head auto-dismisses after this
// shorter window so bursts drain quickly instead of making the user (and E2E
// waits) sit through each item's full timeout in series. The last item in the
// queue always keeps its full timeout, and persistent items (timeout < 0, used
// for in-progress notices dismissed programmatically) are never shortened.
const BACKLOG_CYCLE_MS = 1500;

// Render only the head of the queue, but as a single-element list so each
// snackbar's dismiss handler closes over its own notification id. A stale
// snackbar emitting a late `update:model-value=false` during its leave
// transition then dismisses its own (already-removed) entry rather than the
// item that has since advanced to the head.
const activeNotifications = computed(() => {
  const [head] = store.notifications;
  if (!head) return [];
  const hasBacklog = store.notifications.length > 1;
  const timeout = hasBacklog && head.timeout > 0 ? Math.min(head.timeout, BACKLOG_CYCLE_MS) : head.timeout;
  return [{ ...head, timeout }];
});

function colorFor(type: NotificationType): string {
  switch (type) {
    case "error":
      return "error";
    case "success":
      return "success";
    case "warning":
      return "warning";
    default:
      return "info";
  }
}

function onModelValue(id: number, value: boolean): void {
  // v-snackbar flips model-value to false when its timeout elapses or the
  // user dismisses it — drop the entry so the queue stays in sync.
  if (!value) {
    store.dismiss(id);
  }
}
</script>
